import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioApi } from '../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, X, Sun, Moon, Palette,
  Upload, User, MapPin, Clock, Mail, Linkedin,
  Twitter, Globe, Github, Sparkles
} from 'lucide-react';

const THEMES = [
  { id: 'minimal', label: 'Minimal', icon: Sun, description: 'Clean & light' },
  { id: 'dark', label: 'Dark', icon: Moon, description: 'Bold & modern' },
  { id: 'creative', label: 'Creative', icon: Palette, description: 'Warm & vibrant' },
];

const AVAILABILITY_OPTIONS = [
  'Open to opportunities', 'Actively looking', 'Freelance available',
  'Contract available', 'Not available', 'Open to collaborate', 'Open to internships',
];

const PROFESSIONS = [
  'Engineering', 'Design', 'Medicine & Healthcare',
  'Business & Management', 'Education & Teaching',
  'Law', 'Architecture', 'Arts & Creative',
  'Science & Research', 'Finance & Accounting',
  'Marketing & Media', 'Hospitality & Tourism',
  'Agriculture', 'Other'
];

const BRANCHES = {
  'Engineering': ['Computer Science', 'Electrical', 'Mechanical', 'Civil', 'Electronics', 'Chemical', 'Aerospace', 'Biomedical', 'Industrial', 'Other'],
  'Medicine & Healthcare': ['MBBS / General Medicine', 'Dentistry', 'Nursing', 'Pharmacy', 'Physiotherapy', 'Ayurveda', 'Other'],
  'Design': ['UI/UX Design', 'Graphic Design', 'Fashion Design', 'Interior Design', 'Product Design', 'Other'],
  'Business & Management': ['Marketing', 'Finance', 'HR', 'Operations', 'Entrepreneurship', 'Consulting', 'Other'],
  'Science & Research': ['Physics', 'Chemistry', 'Biology', 'Mathematics', 'Environmental Science', 'Data Science', 'Other'],
  'Arts & Creative': ['Photography', 'Filmmaking', 'Music', 'Writing', 'Animation', 'Fine Arts', 'Other'],
  'Architecture': ['Urban Planning', 'Landscape Architecture', 'Interior Architecture', 'Structural Design', 'Other'],
  'Finance & Accounting': ['CA / CPA', 'Investment Banking', 'Financial Planning', 'Taxation', 'Audit', 'Other'],
  'Marketing & Media': ['Digital Marketing', 'Content Creation', 'Public Relations', 'Journalism', 'Advertising', 'Other'],
  'Education & Teaching': ['School Teacher', 'College Professor', 'Corporate Trainer', 'Online Educator', 'Tutor', 'Other'],
};

const SECTIONS = [
  { id: 'basic', label: '📋 Basics' },
  { id: 'skills', label: '🎯 Skills' },
  { id: 'prompts', label: '✨ Personality' },
  { id: 'projects', label: '🚀 Projects' },
  { id: 'social', label: '🔗 Social' },
  { id: 'settings', label: '⚙️ Settings' },
];

const THEME_STYLES = {
  minimal: { card: 'bg-white border-gray-200', header: 'bg-gradient-to-br from-orange-50 to-white', accent: 'text-[#e8744f]', badge: 'bg-orange-50 text-gray-700 border border-orange-100', prompt: 'bg-gray-50', text: 'text-gray-500', name: 'text-gray-900' },
  dark: { card: 'bg-gray-900 border-gray-700', header: 'bg-gradient-to-br from-gray-800 to-gray-900', accent: 'text-orange-400', badge: 'bg-gray-700 text-gray-200 border border-gray-600', prompt: 'bg-gray-800', text: 'text-gray-400', name: 'text-white' },
  creative: { card: 'bg-orange-50 border-orange-200', header: 'bg-gradient-to-br from-[#e8744f] to-[#d66440]', accent: 'text-[#e8744f]', badge: 'bg-orange-100 text-orange-800 border border-orange-200', prompt: 'bg-white border border-orange-100', text: 'text-orange-900/60', name: 'text-white' },
};

export default function EditPortfolio() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const avatarRef = useRef(null);

  const [form, setForm] = useState({
    name: '', title: '', tagline: '', location: '',
    years_experience: '', bio: '', availability: '',
    contact_email: '', avatar_url: '',
    profession: '', branch: '',
    skills: [], tools: [], languages: [],
    skillInput: '', toolInput: '', languageInput: '',
    uniqueness: '', passion: '', proudest_work: '',
    dream_project: '', typical_day: '', career_advice: '',
    social_links: { linkedin: '', twitter: '', portfolio_url: '', github: '' },
    theme: 'minimal', is_public: true, actively_looking: false,
    projects: [], gallery: [],
  });

  const loadPortfolio = useCallback(async () => {
    try {
      const { data } = await portfolioApi.getMy();
      setForm({
        name: data.name || '',
        title: data.title || '',
        tagline: data.tagline || '',
        location: data.location || '',
        years_experience: data.years_experience || '',
        bio: data.bio || '',
        availability: data.availability || '',
        contact_email: data.contact_email || '',
        avatar_url: data.avatar_url || '',
        profession: data.profession || '',
        branch: data.branch || '',
        skills: data.skills || [],
        tools: data.tools || [],
        languages: data.languages || [],
        skillInput: '', toolInput: '', languageInput: '',
        uniqueness: data.uniqueness || '',
        passion: data.passion || '',
        proudest_work: data.proudest_work || '',
        dream_project: data.dream_project || '',
        typical_day: data.typical_day || '',
        career_advice: data.career_advice || '',
        social_links: {
          linkedin: data.social_links?.linkedin || '',
          twitter: data.social_links?.twitter || '',
          portfolio_url: data.social_links?.portfolio_url || '',
          github: data.social_links?.github || '',
        },
        theme: data.theme || 'minimal',
        is_public: data.is_public ?? true,
        actively_looking: data.actively_looking ?? false,
        projects: data.projects || [],
        gallery: data.gallery || [],
      });
    } catch {
      toast.error('Failed to load portfolio');
      navigate('/my-portfolio');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setSocial = (field, value) => setForm(f => ({ ...f, social_links: { ...f.social_links, [field]: value } }));

  const addTag = (field, inputField) => {
    const val = form[inputField].trim();
    if (val && !form[field].includes(val)) {
      setForm(f => ({ ...f, [field]: [...f[field], val], [inputField]: '' }));
    }
  };

  const removeTag = (field, index) => {
    setForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== index) }));
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => set('avatar_url', reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await portfolioApi.update({
        name: form.name,
        title: form.title,
        tagline: form.tagline || undefined,
        location: form.location || undefined,
        years_experience: form.years_experience ? parseInt(form.years_experience) : undefined,
        bio: form.bio || undefined,
        availability: form.availability || undefined,
        contact_email: form.contact_email || undefined,
        avatar_url: form.avatar_url || undefined,
        profession: form.profession || undefined,
        branch: form.branch || undefined,
        skills: form.skills,
        tools: form.tools,
        languages: form.languages,
        uniqueness: form.uniqueness || undefined,
        passion: form.passion || undefined,
        proudest_work: form.proudest_work || undefined,
        dream_project: form.dream_project || undefined,
        typical_day: form.typical_day || undefined,
        career_advice: form.career_advice || undefined,
        social_links: form.social_links,
        theme: form.theme,
        is_public: form.is_public,
        actively_looking: form.actively_looking,
        // ✅ These were missing — now included
        projects: form.projects,
        gallery: form.gallery,
      });
      toast.success('Portfolio saved!');
      navigate('/my-portfolio');
    } catch {
      toast.error('Failed to save portfolio');
    } finally {
      setSaving(false);
    }
  };

  const isSectionComplete = (sectionId) => {
    switch (sectionId) {
      case 'basic':
        return !!(form.name && form.title && form.profession && form.location && form.bio && form.availability && form.contact_email);
      case 'skills':
        return form.skills.length > 0 && form.tools.length > 0 && form.languages.length > 0;
      case 'prompts':
        return !!(form.uniqueness && form.passion && form.proudest_work && form.dream_project && form.typical_day && form.career_advice);
      case 'projects':
        return form.projects.length > 0;
      case 'social':
        return !!(form.social_links.linkedin || form.social_links.github || form.social_links.twitter || form.social_links.portfolio_url);
      case 'settings':
        return true;
      default:
        return false;
    }
  };

  const completedCount = SECTIONS.filter(s => isSectionComplete(s.id)).length;
  const ts = THEME_STYLES[form.theme];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#f5f1ed]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8744f] mx-auto mb-4" />
        <p className="text-gray-600">Loading your portfolio...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f1ed]">
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/my-portfolio')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <span className="font-bold text-[#e8744f]">✏️ Edit Portfolio</span>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#e8744f] hover:bg-[#d66440] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save size={16} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="flex h-[calc(100vh-57px)]">
        <div className="w-full md:w-1/2 overflow-y-auto p-6 space-y-4">

          {/* Theme Selector */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Choose Your Vibe</p>
            <div className="grid grid-cols-3 gap-2">
              {THEMES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.id} onClick={() => set('theme', t.id)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${form.theme === t.id ? 'border-[#e8744f] bg-orange-50 scale-[1.02]' : 'border-gray-200 hover:border-orange-200'}`}>
                    <Icon size={18} className={form.theme === t.id ? 'text-[#e8744f]' : 'text-gray-400'} />
                    <span className="text-xs font-semibold">{t.label}</span>
                    <span className="text-[10px] text-gray-400">{t.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Progress + Section Nav */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Progress</span>
              <span className="text-xs font-bold text-[#e8744f]">{completedCount}/{SECTIONS.length} sections done</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3">
              <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${(completedCount / SECTIONS.length) * 100}%` }} />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {SECTIONS.map(s => {
                const complete = isSectionComplete(s.id);
                const active = activeSection === s.id;
                return (
                  <button key={s.id} onClick={() => setActiveSection(s.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1
                      ${active ? 'bg-[#e8744f] text-white' : complete ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-orange-50'}`}>
                    {complete && !active && <span>✓</span>}
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── BASIC ── */}
          {activeSection === 'basic' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-bold text-lg">👋 Basic Info</h2>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-orange-100 flex items-center justify-center border-2 border-orange-200">
                    {form.avatar_url ? <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" /> : <User size={32} className="text-orange-300" />}
                  </div>
                  <button onClick={() => avatarRef.current?.click()} className="absolute -bottom-1 -right-1 bg-[#e8744f] rounded-full p-1.5 text-white shadow">
                    <Upload size={12} />
                  </button>
                  <input ref={avatarRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Profile Photo</p>
                  <p className="text-xs text-gray-400">Click the icon to upload</p>
                </div>
              </div>

              {[
                { label: 'Full Name *', field: 'name', placeholder: 'Alex Morgan' },
                { label: 'Title *', field: 'title', placeholder: 'Electrical Engineer / Graphic Designer / Teacher...' },
                { label: 'Tagline', field: 'tagline', placeholder: 'What you do in one powerful line' },
                { label: 'Location', field: 'location', placeholder: 'Mumbai, India' },
                { label: 'Contact Email', field: 'contact_email', placeholder: 'hello@yourname.com' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input value={form[field]} onChange={e => set(field, e.target.value)} placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition" />
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profession *</label>
                <select value={form.profession} onChange={e => { set('profession', e.target.value); set('branch', ''); }}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition">
                  <option value="">Select your profession</option>
                  {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {form.profession && BRANCHES[form.profession] && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch / Specialization</label>
                  <select value={form.branch} onChange={e => set('branch', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition">
                    <option value="">Select your branch</option>
                    {BRANCHES[form.profession].map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                <input type="number" value={form.years_experience} onChange={e => set('years_experience', e.target.value)} placeholder="3"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                <select value={form.availability} onChange={e => set('availability', e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition">
                  <option value="">Select availability</option>
                  {AVAILABILITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">About Me</label>
                <textarea value={form.bio} onChange={e => set('bio', e.target.value)} placeholder="Tell your professional story..." rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition resize-none" />
              </div>
            </div>
          )}

          {/* ── SKILLS ── */}
          {activeSection === 'skills' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-6">
              <h2 className="font-bold text-lg">🎯 Skills & Tools</h2>
              {[
                { label: 'Skills', field: 'skills', input: 'skillInput', placeholder: 'AutoCAD, Circuit Design, Teaching, Photography...' },
                { label: 'Tools', field: 'tools', input: 'toolInput', placeholder: 'MATLAB, Figma, Excel, Photoshop...' },
                { label: 'Languages', field: 'languages', input: 'languageInput', placeholder: 'English, Hindi, Tamil...' },
              ].map(({ label, field, input, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <div className="flex gap-2 mb-2">
                    <input value={form[input]} onChange={e => set(input, e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag(field, input))}
                      placeholder={placeholder}
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition" />
                    <button onClick={() => addTag(field, input)} className="bg-[#e8744f] text-white px-3 py-2 rounded-xl hover:bg-[#d66440] transition">
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form[field].map((item, i) => (
                      <span key={i} className="flex items-center gap-1.5 bg-orange-50 text-gray-700 px-3 py-1 rounded-full text-sm border border-orange-100">
                        {item}
                        <button onClick={() => removeTag(field, i)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── PROMPTS ── */}
          {activeSection === 'prompts' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-bold text-lg">✨ Personality Prompts</h2>
              <p className="text-xs text-gray-400">These make your profile stand out — answer honestly!</p>
              {[
                { label: '🌟 What makes you different?', field: 'uniqueness', placeholder: 'My hands-on experience combined with strong theory...' },
                { label: '🔥 What excites you about your work?', field: 'passion', placeholder: 'Solving real-world problems that impact people...' },
                { label: "🏆 I'm most proud of...", field: 'proudest_work', placeholder: 'Designed a solar panel system for a rural village...' },
                { label: '💭 My dream project would be...', field: 'dream_project', placeholder: 'Building something that creates lasting impact...' },
                { label: '☀️ A typical day for me looks like...', field: 'typical_day', placeholder: 'Site visits, design reviews, client meetings...' },
                { label: '💬 Best career advice I received', field: 'career_advice', placeholder: 'Always keep learning — every field evolves...' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <textarea value={form[field]} onChange={e => set(field, e.target.value)} placeholder={placeholder} rows={3}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition resize-none" />
                </div>
              ))}
            </div>
          )}

          {/* ── PROJECTS ── */}
          {activeSection === 'projects' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-bold text-lg">🚀 Projects</h2>
              <p className="text-xs text-gray-400">Add projects to showcase your work.</p>

              {/* Add new project */}
              <AddProjectForm onAdd={(project) => setForm(f => ({ ...f, projects: [...f.projects, project] }))} />

              {form.projects.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No projects yet — add one above!</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {form.projects.map((p, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-gray-100 shadow-sm relative group">
                      {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-24 object-cover" />}
                      <div className="p-2">
                        <p className="text-sm font-semibold truncate">{p.title}</p>
                        {p.description && <p className="text-xs text-gray-400 truncate">{p.description}</p>}
                      </div>
                      <button
                        onClick={() => removeTag('projects', i)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── SOCIAL ── */}
          {activeSection === 'social' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-bold text-lg">🔗 Social Links</h2>
              {[
                { label: 'LinkedIn', field: 'linkedin', icon: Linkedin, placeholder: 'linkedin.com/in/yourname' },
                { label: 'Twitter / X', field: 'twitter', icon: Twitter, placeholder: '@yourhandle' },
                { label: 'GitHub', field: 'github', icon: Github, placeholder: 'github.com/yourname' },
                { label: 'Portfolio / Website', field: 'portfolio_url', icon: Globe, placeholder: 'yoursite.com' },
              ].map(({ label, field, icon: Icon, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 focus-within:border-[#e8744f] focus-within:ring-2 focus-within:ring-orange-100 transition">
                    <Icon size={16} className="text-gray-400 flex-shrink-0" />
                    <input value={form.social_links[field]} onChange={e => setSocial(field, e.target.value)} placeholder={placeholder}
                      className="flex-1 py-2.5 text-sm focus:outline-none bg-transparent" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── SETTINGS ── */}
          {activeSection === 'settings' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-bold text-lg">⚙️ Settings</h2>
              {[
                { label: 'Public Portfolio', sub: 'Others can find and view your portfolio', field: 'is_public' },
                { label: 'Actively Looking', sub: 'Show a green badge on your profile', field: 'actively_looking' },
              ].map(({ label, sub, field }) => (
                <label key={field} className="flex items-center justify-between p-4 border-2 border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition">
                  <div>
                    <p className="font-medium text-sm">{label}</p>
                    <p className="text-xs text-gray-400">{sub}</p>
                  </div>
                  <div onClick={() => set(field, !form[field])} className={`w-11 h-6 rounded-full transition-colors relative ${form[field] ? 'bg-[#e8744f]' : 'bg-gray-200'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form[field] ? 'translate-x-5' : ''}`} />
                  </div>
                </label>
              ))}
            </div>
          )}

        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="hidden md:flex w-1/2 flex-col border-l border-gray-200 bg-gray-100 overflow-y-auto">
          <div className="sticky top-0 bg-gray-100 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Sparkles size={14} className="text-[#e8744f]" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live Preview</span>
          </div>
          <div className="p-4">
            <div className={`rounded-2xl border-2 overflow-hidden shadow-lg transition-all duration-500 ${ts.card}`}>
              <div className={`p-6 ${ts.header}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 ${form.theme === 'creative' ? 'border-white/30' : 'border-orange-200'}`}>
                    {form.avatar_url ? <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-2xl font-bold">{form.name?.charAt(0) || '?'}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className={`font-bold text-xl truncate ${ts.name}`}>{form.name || 'Your Name'}</h1>
                    <p className={`text-sm ${form.theme === 'creative' ? 'text-white/80' : ts.text}`}>{form.title || 'Your Title'}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(form.profession || form.branch) && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          🎓 {[form.branch, form.profession].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {form.location && <span className={`flex items-center gap-1 text-xs ${form.theme === 'creative' ? 'text-white/70' : ts.text}`}><MapPin size={10} />{form.location}</span>}
                      {form.years_experience && <span className={`flex items-center gap-1 text-xs ${form.theme === 'creative' ? 'text-white/70' : ts.text}`}><Clock size={10} />{form.years_experience}+ yrs</span>}
                      {form.availability && (
                        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          form.availability === 'Actively looking' ? 'bg-green-500/20 text-green-600' :
                          form.availability === 'Not available' ? 'bg-red-500/20 text-red-500' : 'bg-orange-500/20 text-orange-600'}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{form.availability}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {form.tagline && <p className={`mt-3 text-xs italic ${form.theme === 'creative' ? 'text-white/80' : ts.text}`}>"{form.tagline}"</p>}
              </div>
              <div className="p-5 space-y-4">

                {/* About */}
                {form.bio && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-1 ${ts.accent}`}>About Me</h3>
                    <p className={`text-xs leading-relaxed line-clamp-3 ${ts.text}`}>{form.bio}</p>
                  </div>
                )}

                {/* Skills */}
                {form.skills.length > 0 && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-2 ${ts.accent}`}>Skills</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {form.skills.slice(0, 6).map((s, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-medium ${ts.badge}`}>{s}</span>)}
                      {form.skills.length > 6 && <span className={`text-xs px-2 py-1 rounded-full ${ts.badge}`}>+{form.skills.length - 6}</span>}
                    </div>
                  </div>
                )}

                {/* Tools */}
                {form.tools.length > 0 && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-2 ${ts.accent}`}>Tools</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {form.tools.slice(0, 6).map((t, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-medium ${ts.badge}`}>{t}</span>)}
                      {form.tools.length > 6 && <span className={`text-xs px-2 py-1 rounded-full ${ts.badge}`}>+{form.tools.length - 6}</span>}
                    </div>
                  </div>
                )}

                {/* Languages */}
                {form.languages.length > 0 && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-2 ${ts.accent}`}>Languages</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {form.languages.map((l, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-medium ${ts.badge}`}>{l}</span>)}
                    </div>
                  </div>
                )}

                {/* Projects */}
                {form.projects.length > 0 && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-2 ${ts.accent}`}>Projects</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {form.projects.slice(0, 4).map((p, i) => (
                        <div key={i} className={`rounded-xl overflow-hidden ${ts.prompt}`}>
                          {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-16 object-cover" />}
                          <div className="p-2">
                            <p className={`text-xs font-bold truncate ${form.theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{p.title}</p>
                            {p.description && <p className={`text-[10px] mt-0.5 line-clamp-1 ${ts.text}`}>{p.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Personality Prompts */}
                {[
                  { icon: '🌟', q: 'What makes me different', val: form.uniqueness },
                  { icon: '🔥', q: 'What excites me', val: form.passion },
                  { icon: '🏆', q: "I'm most proud of", val: form.proudest_work },
                  { icon: '💭', q: 'Dream project', val: form.dream_project },
                  { icon: '☀️', q: 'A typical day', val: form.typical_day },
                  { icon: '💬', q: 'Best career advice', val: form.career_advice },
                ].filter(p => p.val).map((p, i) => (
                  <div key={i} className={`rounded-xl p-3 ${ts.prompt}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-sm">{p.icon}</span>
                      <span className={`text-xs font-semibold ${ts.accent}`}>{p.q}</span>
                    </div>
                    <p className={`text-xs leading-relaxed line-clamp-2 ${ts.text}`}>{p.val}</p>
                  </div>
                ))}

                {/* Social */}
                {(form.social_links.linkedin || form.social_links.github || form.social_links.twitter || form.social_links.portfolio_url) && (
                  <div>
                    <h3 className={`font-semibold text-sm mb-2 ${ts.accent}`}>Connect</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {form.social_links.linkedin && <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${ts.badge}`}><Linkedin size={10} /> LinkedIn</span>}
                      {form.social_links.github && <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${ts.badge}`}><Github size={10} /> GitHub</span>}
                      {form.social_links.twitter && <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${ts.badge}`}><Twitter size={10} /> Twitter</span>}
                      {form.social_links.portfolio_url && <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${ts.badge}`}><Globe size={10} /> Website</span>}
                    </div>
                  </div>
                )}

                {/* Contact */}
                {form.contact_email && (
                  <div className={`flex items-center gap-2 rounded-lg p-2.5 ${ts.prompt}`}>
                    <Mail size={12} className={ts.accent} />
                    <span className={`text-xs ${ts.text}`}>{form.contact_email}</span>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mini component for adding a project inline ──
function AddProjectForm({ onAdd }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [open, setOpen] = useState(false);

  const handleAdd = () => {
    if (!title.trim()) return;
    onAdd({ title: title.trim(), description: description.trim(), image_url: imageUrl.trim() || undefined });
    setTitle(''); setDescription(''); setImageUrl(''); setOpen(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-orange-200 rounded-xl py-3 text-sm text-orange-500 hover:bg-orange-50 transition">
      <Plus size={16} /> Add Project
    </button>
  );

  return (
    <div className="border border-orange-100 rounded-xl p-4 space-y-3 bg-orange-50/50">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Project title *"
        className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition" />
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description" rows={2}
        className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition resize-none" />
      <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="Image URL (optional)"
        className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition" />
      <div className="flex gap-2">
        <button onClick={handleAdd} className="flex-1 bg-[#e8744f] hover:bg-[#d66440] text-white rounded-xl py-2 text-sm font-medium transition">Add</button>
        <button onClick={() => setOpen(false)} className="px-4 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-100 transition">Cancel</button>
      </div>
    </div>
  );
}